import { type App, type DefineComponent, type Plugin } from 'vue'
import { RouteRecordRaw } from 'vue-router'
import { route, prefixRoutes } from '@router/utils'
import type { Options } from '@/router'
import { CLIENT_ROLE_KEY, COORDINATOR_ROLE_KEY, DOCTOR_ROLE_KEY } from '@/const'

import {
  createRoutes as createProfileRoutes,
  BASE_MODEL_PATH as PROFILE_ROOT_PATH,
  createSettingsRoutes,
  BASE_SETTINGS_MODEL_PATH,
  createNotificationsRoutes,
  SETTINGS_BASE,
  BASE_NOTIFICATIONS_MODEL_PATH,
  createStaffRoutes as createStaffProfileRoutes,
  STAFF_PROFILE_VIEW_NAME,
} from './modules/Profile/router'

import {
  createRoutes as createChainRoutes,
  BASE_MODEL_NAME as CHAIN_ROOT_PATH,
} from './modules/Chain/router'

import {
  createRoutes as createFacilityRoutes,
  BASE_MODEL_NAME as FACILITY_ROOT_PATH,
} from './modules/Facility/router'

import {
  createRoutes as createDocumentFlowRoutes,
  BASE_MODEL_NAME as DOCUMENT_FLOW_ROOT_PATH,
} from './modules/DocumentFlow/router'

import {
  createRoutes as createDoctorRoutes,
  BASE_MODEL_NAME as DOCTOR_ROOT_PATH,
  DOCTOR_BASE,
  BASE_EMPLOYEE_MODEL_NAME as EMPLOYEE_DOCTOR_ROOT_PATH,
  EMPLOYEE_DOCTOR_BASE,
} from '@m/cabinet/modules/Doctor/router'
// import {
//   createRoutes as createBranchRoutes,
//   BASE_MODEL_NAME as BRANCH_ROOT_PATH,
// } from '@/modules/cabinet/modules/Branch/router'
import {
  createRoutes as createAppointmentRoutes,
  BASE_MODEL_NAME as APPOINTMENT_ROOT_PATH,
} from '@m/cabinet/modules/Appointment/router'
import {
  createRoutes as createMedicalAppointmentRoutes,
  BASE_MODEL_NAME as MEDICAL_APPOINTMENT_ROOT_PATH,
} from '@m/cabinet/modules/MedicalAppointment/router'
import {
  createRoutes as createDoctorAppointmentRoutes,
  BASE_MODEL_NAME as DOCTOR_APPOINTMENT_ROOT_PATH,
} from '@m/cabinet/modules/DoctorAppointment/router'
import {
  createRoutes as createAppointmentReviewsRoutes,
  BASE_MODEL_NAME as APPOINTMENT_REVIEWS_ROOT_PATH,
} from '@m/cabinet/modules/AppointmentReviews/router'
import {
  createRoutes as createReviewsRoutes,
  BASE_MODEL_NAME as REVIEWS_ROOT_PATH,
} from '@m/cabinet/modules/Reviews/router'
import {
  createRoutes as createFavoritesRoutes,
  BASE_MODEL_NAME as FAVORITES_ROOT_PATH,
} from '@m/cabinet/modules/Favorites/router'
import {
  createRoutes as createBilingRoutes,
  BASE_MODEL_PATH as BILLING_ROOT_PATH,
} from '@m/cabinet/modules/Billing/router'

import {
  doctorAccessPermission,
  accessPermission,
} from '@/modules/user/permissions'
import {
  IS_DOCTOR,
  IS_CLIENT,
  IS_ADMINISTRATOR,
} from '@/modules/user/accessTokens'

// import { createRoutes as createTransactionsRoutes } from './modules/Transactions/router'
import { i18n } from '@/i18n'

import { PROFILE_VIEW_NAME } from './routes'
import StaffLayout from './components/layouts/StaffLayout.vue'

import {
  createFacilitiesRoutes,
  FACILITIES_MODEL_PATH,
  FACILITIES_STAFF_VIEW_NAME,
  STAFF_FACILITIES_VIEW_NAME,
} from './modules/Staff/modules/Facilities/router'

const CabinetView = () => import('./views/CabinetView.vue')
const CabinetInstaller = () => import('./components')
const Route403 = () => import('./views/Route403.vue')
const Route404 = () => import('./views/Route404.vue')

function installer<T extends DefineComponent>(
  View: any,
  Installer: () => Promise<T>,
  app: App<Element>
): () => Promise<Awaited<T>> {
  return function (): Promise<Awaited<T>> {
    return Promise.all([View(), Installer()]).then(function (result) {
      const [ViewComponent, installer] = result
      app.use(installer?.default || installer?.install || installer)
      return ViewComponent
    })
  }
}

const createCabinetRoutes = (options: Options): RouteRecordRaw[] => {
  return [
    prefixRoutes(PROFILE_ROOT_PATH, createProfileRoutes(options)),
    prefixRoutes(
      BASE_NOTIFICATIONS_MODEL_PATH,
      createNotificationsRoutes(options)
    ),
    prefixRoutes(
      APPOINTMENT_REVIEWS_ROOT_PATH,
      createAppointmentReviewsRoutes(options),
      {
        name: APPOINTMENT_REVIEWS_ROOT_PATH,
        meta: {
          ...accessPermission([IS_CLIENT], options),
        },
      }
    ),

    prefixRoutes(BILLING_ROOT_PATH, createBilingRoutes(options)),
    prefixRoutes(FACILITY_ROOT_PATH, createFacilityRoutes(options)),
    prefixRoutes(DOCUMENT_FLOW_ROOT_PATH, createDocumentFlowRoutes(options)),
    prefixRoutes(CHAIN_ROOT_PATH, createChainRoutes(options)),
    // prefixRoutes(BRANCH_ROOT_PATH, createBranchRoutes(options)),

    prefixRoutes(DOCTOR_ROOT_PATH, createDoctorRoutes(options), {
      name: DOCTOR_BASE,
    }),

    prefixRoutes(EMPLOYEE_DOCTOR_ROOT_PATH, createDoctorRoutes(options, true), {
      name: EMPLOYEE_DOCTOR_BASE,
    }),
    prefixRoutes(BASE_SETTINGS_MODEL_PATH, createSettingsRoutes(options), {
      name: SETTINGS_BASE,
      meta: {
        ...accessPermission([IS_DOCTOR], options),
      },
    }),
    prefixRoutes(APPOINTMENT_ROOT_PATH, createAppointmentRoutes(options), {
      name: APPOINTMENT_ROOT_PATH,
    }),
    prefixRoutes(
      MEDICAL_APPOINTMENT_ROOT_PATH,
      createMedicalAppointmentRoutes(options),
      {
        name: MEDICAL_APPOINTMENT_ROOT_PATH,
      }
    ),
    prefixRoutes(
      DOCTOR_APPOINTMENT_ROOT_PATH,
      createDoctorAppointmentRoutes(options),
      {
        name: DOCTOR_APPOINTMENT_ROOT_PATH,
      }
    ),
    prefixRoutes(REVIEWS_ROOT_PATH, createReviewsRoutes(options), {
      name: REVIEWS_ROOT_PATH,
      meta: {
        ...accessPermission([IS_DOCTOR], options),
      },
    }),
    prefixRoutes(FAVORITES_ROOT_PATH, createFavoritesRoutes(options), {
      name: FAVORITES_ROOT_PATH,
    }),
  ]
}
export function createRoutes(options: Options): RouteRecordRaw[] {
  const { app } = options
  const { t: $t } = i18n.global
  return [
    route(
      '',
      installer<any>(
        CabinetView,
        CabinetInstaller as unknown as () => Promise<Plugin>,
        app
      ),
      'cabinet',
      {
        children: [
          ...createCabinetRoutes(options),
          {
            path: '/:pathMatch(.*)*',
            name: 'cabinet:not-found',
            component: Route404,
            meta: {
              title: () => $t('404'),
            },
          },
          {
            path: '/:pathMatch(.*)*',
            name: 'cabinet:no-access',
            component: Route403,
            meta: {
              title: () => $t('403'),
            },
          },
        ],
        redirect: { name: PROFILE_VIEW_NAME },
      }
    ),
  ]
}

import {
  createServicesRoutes,
  STAFF_SERVICES_MODEL_PATH,
  STAFF_SERVICES_VIEW_NAME,
} from '@/modules/cabinet/modules/Staff/modules/Services/router'
import {
  createSettingRoutes,
  STAFF_SETTING_MODEL_PATH,
  STAFF_SETTING_VIEW_NAME,
} from './modules/Staff/modules/Setting/router'
import {
  createAnalyticsRoutes,
  STAFF_ANALYTICS_MODEL_PATH,
  STAFF_ANALYTICS_VIEW_NAME,
} from './modules/Staff/modules/Analytics/router'
import {
  createStaffDoctorsRoutes,
  STAFF_DOCTORS_MODEL_PATH, 
  STAFF_DOCTORS_LIST_VIEW_NAME,
  STAFF_DOCTORS_VIEW_NAME,
} from './modules/Staff/modules/Doctors/router'

const createStaffRoutes = (options: Options): RouteRecordRaw[] => {
  return [
    prefixRoutes(PROFILE_ROOT_PATH, createStaffProfileRoutes(options), {
      ...options,
      meta: {
        ...accessPermission([IS_ADMINISTRATOR], options, {
          name: 'staff:no-access',
        }),
      },
    }),
    prefixRoutes(FACILITIES_MODEL_PATH, createFacilitiesRoutes(options), {
      name: STAFF_FACILITIES_VIEW_NAME,
      ...options,
      meta: {
        ...accessPermission([IS_ADMINISTRATOR], options, {
          name: 'staff:no-access',
        }),
        redirect: { name: FACILITIES_STAFF_VIEW_NAME },
      },
    }),
    prefixRoutes(STAFF_SERVICES_MODEL_PATH, createServicesRoutes(options), {
      ...options,
      name: STAFF_SERVICES_VIEW_NAME,
      meta: {
        ...accessPermission([IS_ADMINISTRATOR], options, {
          name: 'staff:no-access',
        }),
      },
    }),
    prefixRoutes(STAFF_SETTING_MODEL_PATH, createSettingRoutes(options), {
      ...options,
      name: STAFF_SETTING_VIEW_NAME,
      meta: {
        ...accessPermission([IS_ADMINISTRATOR], options, {
          name: 'staff:no-access',
        }),
      },
    }),
    prefixRoutes(STAFF_ANALYTICS_MODEL_PATH, createAnalyticsRoutes(options), {
      ...options,
      name: STAFF_ANALYTICS_VIEW_NAME,
      meta: {
        ...accessPermission([IS_ADMINISTRATOR], options, {
          name: 'staff:no-access',
        }),
      },
    }),
    prefixRoutes(STAFF_DOCTORS_MODEL_PATH, createStaffDoctorsRoutes(options), {
      ...options,
      name: STAFF_DOCTORS_VIEW_NAME,
      meta: {
        ...accessPermission([IS_ADMINISTRATOR], options, {
          name: 'staff:no-access',
        }),
      },
    }),
  ]
}

export function createStaffCabinetRoutes(options: Options): RouteRecordRaw[] {
  const { app } = options
  const { t: $t } = i18n.global
  return [
    route(
      '',
      installer<any>(
        CabinetView,
        CabinetInstaller as unknown as () => Promise<Plugin>,
        app
      ),
      'staff',
      {
        children: [
          ...createStaffRoutes(options),
          {
            path: '/:pathMatch(.*)*',
            name: 'staff:not-found',
            component: Route404,
            meta: {
              title: () => $t('404'),
            },
          },
          {
            path: '/:pathMatch(.*)*',
            name: 'staff:no-access',
            component: Route403,
            meta: {
              title: () => $t('403'),
            },
          },
        ],
        redirect: { name: STAFF_PROFILE_VIEW_NAME },
        meta: {
          layout: StaffLayout,
        },
      }
    ),
  ]
}
